import 'intersection-observer';
import 'utils/extendDayjs';
import 'locale/i18n';

import React, { lazy, Suspense } from 'react';
import { Route, Router, Switch } from 'react-router-dom';
import { createBrowserHistory } from 'history';

import {
    IS_EDITOR_MODE,
    IS_LOCAL_DEV,
    IS_POINT_FLOW,
    IS_UGC_FLOW,
    IS_PROMO_FLOW
} from 'config/envVariable';
import { appBaseName } from 'config/basenames';
import { initErrorLogService } from 'sentry.js';

import GlobalCss from 'styles/GlobalCss';
import ProviderTheme from 'components/ProviderTheme';
import PageForbidden from 'components/PageForbidden';
import NotFound from 'client/components/NotFound';
import LoadingPromoInit from 'client/components/LoadingPromoInit';
import LoadingEditor from 'editor/components/LoadingEditor';
import CheckLogout from 'client/components/CheckLogout';
import useGetFontFamilyByI18nLang from 'hooks/useGetFontFamilyByI18nLang';

initErrorLogService();

const EnvInfo = lazy(() => import(/* webpackChunkName: "env_info" */ 'components/EnvInfo'));
const Editor = lazy(() => import(/* webpackChunkName: "editor" */ 'editor/Editor'));
const DevLocalFirebaseLogin = lazy(() =>
    import(/* webpackChunkName: "editor_dev_login" */ 'editor/components/DevLocalFirebaseLogin')
);
const RoutePromoClient = lazy(() =>
    import(/* webpackChunkName: "route_promo_client" */ 'client/RoutePromoClient')
);
const RouteRewards = lazy(() =>
    import(/* webpackChunkName: "route_rewards" */ 'rewards/RouteRewards')
);
const RoutesCollected = lazy(() =>
    import(/* webpackChunkName: "route_collected" */ 'collected/RoutesCollected')
);
const RouteUgc = lazy(() => import(/* webpackChunkName: "route_ugc" */ 'ugc/RouteUgc'));
const RouteMontage = lazy(() => import(/* webpackChunkName: "route_montage" */ 'ugc/RouteMontage'));
const EditorLoginHandler = lazy(() =>
    import(/* webpackChunkName: "editor_login" */ 'editor/components/EditorLoginHandler')
);
const LoginHandle = lazy(() =>
    import(/* webpackChunkName: "client_login" */ 'client/components/dialogLogin/LoginHandle')
);
const LineLiffRedirect = lazy(() =>
    import(/* webpackChunkName: "client_login_line_liff" */ 'lineLiff/LineLiffRedirect')
);
const RoutePoint = lazy(() => import(/* webpackChunkName: "route_point" */ 'point/RoutePoint'));
const PointLoginHandle = lazy(() =>
    import(/* webpackChunkName: "client_point_login" */ 'point/components/login/LoginHandle')
);
export const history = createBrowserHistory({ basename: appBaseName });

export default function App() {
    const suspensePlaceHolder = IS_EDITOR_MODE ? <LoadingEditor /> : <LoadingPromoInit />;
    const fontFamily = useGetFontFamilyByI18nLang({
        isClient: !IS_EDITOR_MODE,
        isGlobal: true
    });

    return (
        <>
            <GlobalCss fontFamily={fontFamily} />
            <ProviderTheme config={{ fontFamily }}>
                <Router history={history} basename={appBaseName}>
                    <Suspense fallback={suspensePlaceHolder}>
                        <>
                            {IS_PROMO_FLOW && (
                                <Switch>
                                    {IS_LOCAL_DEV && (
                                        <Route
                                            path="/dev_login"
                                            component={DevLocalFirebaseLogin}
                                        />
                                    )}
                                    <Route path="/lineliff/*" component={LineLiffRedirect} />
                                    <Route path="/editorlogin" component={EditorLoginHandler} />
                                    <Route path="/forbidden" component={PageForbidden} />
                                    <Route path="/notfound" component={NotFound} />
                                    <Route path="/checklogout" component={CheckLogout} />
                                    <Route path="/env_info" component={EnvInfo} />
                                    <Route
                                        path="/rewards/:sponsorUUID/:loginMethod"
                                        component={RouteRewards}
                                    />

                                    <Route
                                        path="/:sponsorId/:promoId/edit/:tab?"
                                        component={Editor}
                                    />
                                    <Route
                                        path="/:promoUUID/redeemprize/:prizeUUID/:entriesUUID"
                                        component={RoutesCollected}
                                    />
                                    <Route
                                        path="/:promoUUID/:promoId/login"
                                        component={LoginHandle}
                                    />
                                    <Route
                                        path="/:promoUUID/:promoId"
                                        component={RoutePromoClient}
                                    />
                                    <Route path="*" component={NotFound} />
                                </Switch>
                            )}
                            {IS_POINT_FLOW && (
                                <Switch>
                                    <Route
                                        path="/:sponsorUUID/:pointPoolUUID/:loginMethod/loginHandle"
                                        exact
                                        component={PointLoginHandle}
                                    />
                                    <Route
                                        path="/:sponsorUUID/:pointPoolUUID"
                                        component={RoutePoint}
                                    />
                                    <Route path="*" component={NotFound} />
                                </Switch>
                            )}
                            {IS_UGC_FLOW && (
                                <Switch>
                                    {IS_LOCAL_DEV && (
                                        <Route
                                            path="/dev_login"
                                            component={DevLocalFirebaseLogin}
                                        />
                                    )}
                                    <Route path="/notfound" component={NotFound} />
                                    <Route
                                        path="/:promoUUID/:promoId/gallery"
                                        component={RouteMontage}
                                    />
                                    <Route path="/:promoId" component={RouteUgc} />
                                    <Route path="*" component={NotFound} />
                                </Switch>
                            )}
                        </>
                    </Suspense>
                </Router>
            </ProviderTheme>
        </>
    );
}
